import React from 'react';
import Link from 'next/link';
import SEOHeading from '@/components/common/SEOHeading';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface LabradorVariety {
  name: string;
  description: string;
  image: string;
  alt: string;
  characteristics: string[];
}

const varieties: LabradorVariety[] = [
{
  name: "Yellow Labrador Puppies",
  description: "These gorgeous pups range from pale cream to rich golden tones. Yellow Labs are known for their gentle, patient nature and make wonderful family dogs. They're especially great with children and love being part of every family activity.",
  image: "https://images.unsplash.com/photo-1566756578171-cbf0bcadd71b",
  alt: "Beautiful Yellow Labrador Retriever puppy for sale with golden coat from reputable Labrador breeders",
  characteristics: ["Friendly", "Gentle", "Patient"]
},
{
  name: "Black Labrador Puppies",
  description: "With their striking glossy coats and boundless energy, Black Labs are incredibly smart and eager to please. They excel at training and are perfect for active families who love outdoor adventures and want a dog that can keep up with their lifestyle.",
  image: "https://images.unsplash.com/photo-1710604400830-d63952c0aa2e",
  alt: "Handsome Black Labrador Retriever puppy for sale with glossy coat from English Labrador breeders",
  characteristics: ["Energetic", "Smart", "Loyal"]
},
{
  name: "Chocolate Labrador Puppies",
  description: "There's something extra special about Chocolate Labs with their warm, rich brown coats. These affectionate pups are known for their playful personalities and loving nature. They form incredibly strong bonds with their families and thrive on companionship.",
  image: "https://images.unsplash.com/photo-1446974803508-df06c9bb6f2b",
  alt: "Adorable Chocolate Labrador Retriever puppy for sale with brown coat from reputable Lab breeders",
  characteristics: ["Affectionate", "Playful", "Devoted"]
},
{
  name: "Fox Red Labrador Puppies",
  description: "These stunning pups are a rare treasure with their distinctive reddish-golden coats that turn heads everywhere. Fox Red Labs have vibrant, confident personalities and athletic builds. They're perfect for families looking for something a bit different while still getting all the wonderful Lab traits.",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ed8a158e-1769959686232.png",
  alt: "Striking Fox Red Labrador Retriever puppy for sale with reddish-golden coat from certified Labrador breeders",
  characteristics: ["Vibrant", "Athletic", "Confident"]
}];


const VarietiesShowcase = () => {
  return (
    <section className="bg-background py-16 lg:py-24">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="mb-12 text-center">
          <SEOHeading level={2} className="mb-4">
            Discover Our Labrador Colors - Yellow, Black, Chocolate & Fox Red Puppies
          </SEOHeading>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
            We're proud to offer all four beautiful Labrador colors, each with their own unique charm. While coat color varies, every Lab shares that wonderful temperament, intelligence, and unwavering loyalty that has made them America's favorite dog for decades. Whether you prefer English-style Labs or American-style Labs, we have healthy, happy puppies waiting to meet you.
          </p>
        </div>

        {/* Varieties Grid */}
        <div className="grid gap-6 md:grid-cols-2">
          {varieties.map((variety, index) =>
          <div
            key={index}
            className="group overflow-hidden rounded-xl bg-card shadow-warm transition-smooth hover:shadow-warm-lg">

              <div className="relative aspect-[16/10] overflow-hidden">
                <AppImage
                src={variety.image}
                alt={variety.alt}
                className="h-full w-full object-cover transition-smooth group-hover:scale-105" />

                <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="mb-2 font-heading text-2xl font-bold text-white">
                    {variety.name}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {variety.characteristics.map((char, i) =>
                  <span
                    key={i}
                    className="rounded-full bg-white/20 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm">

                        {char}
                      </span>
                  )}
                  </div>
                </div>
              </div>
              <div className="p-6">
                <p className="mb-4 text-muted-foreground">
                  {variety.description}
                </p>
                <Link
                href="/available-puppies"
                className="inline-flex items-center gap-2 font-medium text-primary transition-smooth hover:gap-3">

                  View Available Puppies
                  <Icon name="ArrowRightIcon" size={18} variant="outline" />
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

};

export default VarietiesShowcase;